import {intersection, union} from 'lodash';

import {isInteger} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {isTeammateNamespace, isTeamNamespace} from '../../helpers/models/namespaceHelpers';
import {BasicActivityTypesEnum} from '../../models/api/activityModel';
import {ChannelModel} from '../../models/api/channelModel';
import {InboxModel} from '../../models/api/inboxModel';
import {
  RuleCompoundConditions,
  RuleCompoundConditionTypesEnum,
  RuleModelStrict,
} from '../../models/api/ruleModel';
import {TeammateModel} from '../../models/api/teammateModel';
import {TagParam} from '../editors/commons/paramModel';
import {MoveAction, RuleAction, RuleActionTypesEnum} from './ruleActionModel';
import {RuleCondition, RuleConditionTypesEnum} from './ruleConditionModel';

/*
 * Types.
 */

export interface ImpactedInboxes {
  impactedInboxes: ReadonlyArray<InboxModel>;
  individualInboxes: ReadonlyArray<InboxModel>;
  teamInboxes: ReadonlyArray<InboxModel>;
  impactsAllIndividualInboxes: boolean;
  impactsAllTeamInboxes: boolean;
}

/*
 * Helpers.
 */

export function inboxesImpactedByCompoundConditions(
  compoundConditions: RuleCompoundConditions,
  inboxes: ReadonlyArray<InboxModel>,
  channels: ReadonlyArray<ChannelModel>,
): ImpactedInboxes {
  const [operator, ...conditions] = compoundConditions;
  const reducerFunction = getReducerFunction(operator);
  if (!reducerFunction) {
    return computeImpactedInboxes(inboxes, NO_INBOX);
  }

  const impactedInboxes = conditions.reduce((result: ReadonlyArray<InboxModel>, condition) => {
    const inboxesAffectedByCondition = Array.isArray(condition)
      ? inboxesImpactedByCompoundConditions(condition, inboxes, channels).impactedInboxes
      : inboxesImpactedByCondition(condition, inboxes, channels);

    return result.length === 0
      ? inboxesAffectedByCondition
      : reducerFunction(result, inboxesAffectedByCondition);
  }, []);

  return computeImpactedInboxes(inboxes, impactedInboxes);
}

function computeImpactedInboxes(
  allInboxes: ReadonlyArray<InboxModel>,
  impactedInboxes: ReadonlyArray<InboxModel>,
): ImpactedInboxes {
  const setImpactedInboxes = new Set(impactedInboxes);
  const individualInboxes: Array<InboxModel> = [];
  const teamInboxes: Array<InboxModel> = [];

  impactedInboxes.forEach((inbox) => {
    if (isTeamNamespace(inbox.namespace)) {
      teamInboxes.push(inbox);
    }

    if (isTeammateNamespace(inbox.namespace)) {
      individualInboxes.push(inbox);
    }
  });

  let nonImpactedTeamInboxes = 0;
  let nonImpactedPrivateInboxes = 0;

  allInboxes
    .filter((inbox) => !setImpactedInboxes.has(inbox))
    .forEach((nonImpactedInbox) => {
      if (isTeamNamespace(nonImpactedInbox.namespace)) {
        nonImpactedTeamInboxes += 1;
      }

      if (isTeammateNamespace(nonImpactedInbox.namespace)) {
        nonImpactedPrivateInboxes += 1;
      }
    });

  const impactsAllTeamInboxes = nonImpactedTeamInboxes === 0;
  const impactsAllIndividualInboxes = nonImpactedPrivateInboxes === 0;

  return {
    impactedInboxes,
    individualInboxes,
    teamInboxes,
    impactsAllIndividualInboxes,
    impactsAllTeamInboxes,
  };
}

const NO_INBOX: ReadonlyArray<InboxModel> = [];

function inboxesImpactedByCondition(
  ruleCondition: RuleCondition,
  inboxes: ReadonlyArray<InboxModel>,
  channels: ReadonlyArray<ChannelModel>,
): ReadonlyArray<InboxModel> {
  switch (ruleCondition.type) {
    case RuleConditionTypesEnum.ALL_INDIVIDUAL_INBOXES:
      // all individual inboxes of the account
      return inboxes.filter((i) => isTeammateNamespace(i.namespace));

    case RuleConditionTypesEnum.ALL_SHARED_INBOXES:
      // all shared inboxes of the account
      return inboxes.filter((i) => isTeamNamespace(i.namespace));

    case RuleConditionTypesEnum.INBOX_IS:
      // specific inbox
      if (!ruleCondition.params) {
        return NO_INBOX;
      }

      return inboxes.filter((i) => ruleCondition.params && i.id === ruleCondition.params.inbox);

    case RuleConditionTypesEnum.INBOX_IN: {
      // list of inboxes
      if (!ruleCondition.params || !ruleCondition.params.inboxes) {
        return NO_INBOX;
      }

      if (ruleCondition.params.inboxes.length === 0) {
        return inboxes;
      }

      const paramInboxes = ruleCondition.params.inboxes;
      return inboxes.filter((i) => paramInboxes.includes(i.id));
    }

    case RuleConditionTypesEnum.INBOX_BELONGS_TO: {
      // list of inboxes
      if (!ruleCondition.params || !ruleCondition.params.teammates) {
        return NO_INBOX;
      }

      const teammateIds: ReadonlyArray<TeammateModel['id']> =
        ruleCondition.params.teammates.filter(isInteger);
      if (!teammateIds) {
        return NO_INBOX;
      }

      const namespaces = teammateIds.map((id) => `tea:${id.toString()}`);
      return inboxes.filter((i) => namespaces.includes(i.namespace));
    }

    case RuleConditionTypesEnum.CHANNEL_IS: {
      /*
        The conversation could have been moved to a different inbox while retaining the same channel, but we accept
        that because this is just a visual helper.
      */
      if (!ruleCondition.params || !ruleCondition.params.channel) {
        return NO_INBOX;
      }

      const {channel} = ruleCondition.params;
      const chan = channels.find((c) => c.id === channel);
      return inboxes.filter((i) => chan && chan.inboxId === i.id);
    }

    case RuleConditionTypesEnum.CHANNEL_IN: {
      // list of inboxes associate with these channels (same limitations than channel_is)
      if (!ruleCondition.params || !ruleCondition.params.channels) {
        return NO_INBOX;
      }

      const channelIds: ReadonlyArray<ChannelModel['id']> = ruleCondition.params.channels;
      if (!channelIds) {
        return NO_INBOX;
      }

      const allChannelInboxIds = channels.map((channel) => channel.inboxId);
      const channelInboxIds =
        channelIds.length === 0
          ? allChannelInboxIds
          : channelIds.map((id) => {
              const channel = channels.find((c) => c.id === id);
              return channel && channel.inboxId;
            });

      return inboxes.filter((i) => channelInboxIds.includes(i.id));
    }

    default:
      // all inboxes in namespace
      return inboxes;
  }
}

function getReducerFunction(operator: RuleCompoundConditionTypesEnum) {
  if (operator.toUpperCase() === 'OR') {
    return union;
  }

  if (operator.toUpperCase() === 'AND') {
    return intersection;
  }

  return null;
}

export function actionsCanTriggerRules(
  rulesForNamespace: ReadonlyArray<RuleModelStrict>,
  currentRule: RuleModelStrict,
  inboxes: ReadonlyArray<InboxModel>,
): ReadonlyArray<RuleModelStrict> {
  // Rules are in order.
  const result: Array<RuleModelStrict> = [];

  for (const rule of rulesForNamespace) {
    // actions can trigger only rules after the currentRule.
    const ruleAfter = rule.sortOrder && currentRule.sortOrder && rule.sortOrder > currentRule.sortOrder;

    if (ruleAfter && rule.active && testActions(rule, currentRule.actions, inboxes)) {
      result.push(rule);

      if (rule.stopProcessing) {
        return result;
      }
    }
  }
  return result;
}

export function canBeTriggeredByRules(
  rulesForNamespace: ReadonlyArray<RuleModelStrict>,
  currentRule: RuleModelStrict,
  inboxes: ReadonlyArray<InboxModel>,
): ReadonlyArray<RuleModelStrict> {
  const rulesWhoCanTrigger: Array<RuleModelStrict> = [];

  // We are checking only rules who are before the currentRule.
  for (const rule of rulesForNamespace) {
    const ruleAfter = rule.sortOrder && currentRule.sortOrder && rule.sortOrder > currentRule.sortOrder;

    // Rules are send in order, if we are after current sort_order, we cannot be triggered.
    if (ruleAfter) {
      return rulesWhoCanTrigger;
    }

    if (rule.active && testActions(currentRule, rule.actions, inboxes)) {
      rulesWhoCanTrigger.push(rule);

      if (rule.stopProcessing) {
        return rulesWhoCanTrigger;
      }
    }
  }
  return rulesWhoCanTrigger;
}

function hasAssignee(ruleCondition: RuleCondition, assigneeId?: number): boolean {
  return (
    assigneeId !== undefined &&
    ruleCondition.type === RuleConditionTypesEnum.ASSIGNEE_IS &&
    ruleCondition.params?.teammate === assigneeId
  );
}

function isTagCondition(condition: RuleCondition): condition is RuleCondition & {params: TagParam} {
  return (condition as {params: TagParam})?.params?.tag !== undefined;
}

function hasTag(ruleCondition: RuleCondition, tagId?: number): boolean {
  return (
    tagId !== undefined &&
    isTagCondition(ruleCondition) &&
    ruleCondition.params.tag === tagId &&
    (ruleCondition.type === RuleConditionTypesEnum.HAS_TAG ||
      ruleCondition.type === RuleConditionTypesEnum.ADDED_TAG_IS)
  );
}

function hasUnTag(ruleCondition: RuleCondition, tagId?: number): boolean {
  return (
    tagId !== undefined &&
    ruleCondition.type === RuleConditionTypesEnum.REMOVED_TAG_IS &&
    ruleCondition.params?.tag === tagId
  );
}

function testActions(
  rule: RuleModelStrict,
  actions: ReadonlyArray<RuleAction>,
  inboxes: ReadonlyArray<InboxModel>,
): boolean {
  return actions.some((action) => {
    switch (action.type) {
      case RuleActionTypesEnum.ASSIGN: {
        const assigneeId = action.params.teammate;
        return (
          rule.events.includes(BasicActivityTypesEnum.ASSIGN) &&
          assigneeId !== undefined &&
          checkConditions(rule.compoundConditions, hasAssignee, assigneeId)
        );
      }

      case RuleActionTypesEnum.UNASSIGN:
        return rule.events.includes(BasicActivityTypesEnum.UNASSIGN);

      case RuleActionTypesEnum.MOVE: {
        const inbox = inboxes.find((i) => i.id === action.params.inbox);

        // We check the namespace, because if a rule move to another namespace it does not trigger any rules.
        if (inbox) {
          return rule.events.includes(BasicActivityTypesEnum.MOVE) && inbox.namespace === rule.namespace;
        }

        return false;
      }

      case RuleActionTypesEnum.TAG_PRIVATE:
      case RuleActionTypesEnum.TAG: {
        // Conversation tagged (check the IF filter for “Added tag is”)
        const tagId = action.params.tag;
        return (
          (rule.events.includes(BasicActivityTypesEnum.TAG) ||
            rule.events.includes(RuleActionTypesEnum.TAG_PRIVATE)) &&
          tagId !== undefined &&
          checkConditions(rule.compoundConditions, hasTag, tagId)
        );
      }

      case RuleActionTypesEnum.UNTAG_PRIVATE:
      case RuleActionTypesEnum.UNTAG: {
        // Conversation untagged (check the IF filter for “Removed tag is”)
        const tagId = action.params.tag;

        return (
          (rule.events.includes(BasicActivityTypesEnum.UNTAG) ||
            rule.events.includes(RuleActionTypesEnum.UNTAG_PRIVATE)) &&
          tagId !== undefined &&
          checkConditions(rule.compoundConditions, hasUnTag, tagId)
        );
      }

      case RuleActionTypesEnum.ARCHIVE:
        return rule.events.includes(BasicActivityTypesEnum.ARCHIVE);

      case RuleActionTypesEnum.UNARCHIVE:
        return rule.events.includes(BasicActivityTypesEnum.REOPEN);

      case RuleActionTypesEnum.TRASH:
        return rule.events.includes(BasicActivityTypesEnum.TRASH);

      default:
        return false;
    }
  });
}

function checkConditions(
  compoundConditions: RuleCompoundConditions,
  func: (ruleCondition: RuleCondition, param?: number) => boolean,
  paramValue?: any,
): boolean {
  const [, ...conditions] = compoundConditions;
  for (const item of conditions) {
    const currentVal = Array.isArray(item) ? checkConditions(item, func, paramValue) : func(item, paramValue);

    if (currentVal) {
      return currentVal;
    }
  }
  return false;
}

/**
 * Given a list of actions, return last move action.
 */
export function getLastMoveAction(actions: ReadonlyArray<RuleAction>): MoveAction | undefined {
  const moveActions = actions.filter((action): action is MoveAction => action.type === 'move');
  if (moveActions.length === 0) {
    return undefined;
  }

  return moveActions.pop();
}

/**
 * Given a list of actions, return the inbox those actions would move a conversation to (or undefined if no move action).
 */
export function getInboxFedByActions(
  actions: ReadonlyArray<RuleAction>,
  inboxes: ReadonlyArray<InboxModel>,
): InboxModel | undefined {
  const lastMoveAction = getLastMoveAction(actions);
  if (!lastMoveAction) {
    return undefined;
  }

  const lastMovedToInboxId = lastMoveAction.params.inbox;
  return inboxes.find((inbox) => inbox.id === lastMovedToInboxId);
}
